import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";

import type { EmServiceMachine, LongHead, Track } from "@/schemas/machine";

import { MachineServiceRow, MachineServiceToggle } from "../components";
import { MachineServiceCard } from "../machine-service-card";

interface LongHeadControlProps {
	readonly longhead: LongHead;
	readonly creaseTools: number[];
	readonly cutTools: number[];
	readonly activeLatches: number[];
	readonly onToggleCreaseClicked: (longHeadNumber: number) => void;
	readonly onToggleLatchClicked: (longHeadNumber: number) => void;
	readonly onToggleCutClicked: (longHeadNumber: number) => void;
}

const LongHeadControl: React.FC<LongHeadControlProps> = ({
	longhead,
	creaseTools,
	cutTools,
	activeLatches,
	onToggleCreaseClicked,
	onToggleLatchClicked,
	onToggleCutClicked,
}) => (
	<MachineServiceRow>
		<Typography variant="h4">
			<FormattedMessage
				id="Long Head {longheadNumber}"
				defaultMessage="Long Head {longheadNumber}"
				values={{ longheadNumber: longhead.number }}
			/>
		</Typography>
		<MachineServiceToggle
			label={<FormattedMessage id="Crease Ring" defaultMessage="Crease Ring" />}
			checked={creaseTools.includes(longhead.number)}
			onChange={() => onToggleCreaseClicked(longhead.number)}
		/>
		<MachineServiceToggle
			label={<FormattedMessage id="Latch" defaultMessage="Latch" />}
			checked={activeLatches.includes(longhead.number)}
			onChange={() => onToggleLatchClicked(longhead.number)}
		/>
		<MachineServiceToggle
			label={<FormattedMessage id="Cut" defaultMessage="Cut" />}
			checked={cutTools.includes(longhead.number)}
			onChange={() => onToggleCutClicked(longhead.number)}
		/>
	</MachineServiceRow>
);

interface TrackControlProps {
	readonly track: Track;
	readonly brakeTools: number[];
	readonly rollerTools: number[];
	readonly onToggleBrakeClicked: (trackNumber: number) => void;
	readonly onToggleRollerClicked: (trackNumber: number) => void;
}

const TrackControl: React.FC<TrackControlProps> = ({
	track,
	brakeTools,
	rollerTools,
	onToggleBrakeClicked,
	onToggleRollerClicked,
}) => (
	<MachineServiceRow>
		<Typography variant="h4">
			<FormattedMessage
				id="Track {trackNumber}"
				defaultMessage="Track {trackNumber}"
				values={{ trackNumber: track.trackNumber }}
			/>
		</Typography>
		<MachineServiceToggle
			label={<FormattedMessage id="Brake" defaultMessage="Brake" />}
			checked={track.trackNumber !== undefined && brakeTools.includes(track.trackNumber)}
			onChange={() => {
				if (track.trackNumber !== undefined) {
					onToggleBrakeClicked(track.trackNumber);
				}
			}}
		/>
		<MachineServiceToggle
			label={<FormattedMessage id="Pressure Roller" defaultMessage="Pressure Roller" />}
			checked={track.trackNumber !== undefined && rollerTools.includes(track.trackNumber)}
			onChange={() => {
				if (track.trackNumber !== undefined) {
					onToggleRollerClicked(track.trackNumber);
				}
			}}
		/>
	</MachineServiceRow>
);

interface CrossHeadControlProps {
	readonly machine: EmServiceMachine;
	readonly onToggleCreaseClicked: () => void;
	readonly onToggleLatchClicked: () => void;
	readonly onToggleCutClicked: () => void;
}

const CrossHeadControl: React.FC<CrossHeadControlProps> = ({
	machine,
	onToggleCreaseClicked,
	onToggleLatchClicked,
	onToggleCutClicked,
}) => (
	<MachineServiceRow>
		<Box />

		<MachineServiceToggle
			label={<FormattedMessage id="Crease Ring" defaultMessage="Crease Ring" />}
			checked={machine.activeCrossHeadCrease === true}
			onChange={onToggleCreaseClicked}
		/>
		<MachineServiceToggle
			label={<FormattedMessage id="Latch" defaultMessage="Latch" />}
			checked={machine.activeCrossHeadLatch === true}
			onChange={onToggleLatchClicked}
		/>
		<MachineServiceToggle
			label={<FormattedMessage id="Cut" defaultMessage="Cut" />}
			checked={machine.activeCrossHeadCut === true}
			onChange={onToggleCutClicked}
		/>
	</MachineServiceRow>
);

interface CycleControlProps {
	readonly onCycleCrossHeadToolsClicked: () => void;
	readonly onCycleLongHeadToolsClicked: () => void;
	readonly onCycleTrackToolsClicked: () => void;
}

const CycleControl: React.FC<CycleControlProps> = ({
	onCycleCrossHeadToolsClicked,
	onCycleLongHeadToolsClicked,
	onCycleTrackToolsClicked,
}) => (
	<MachineServiceRow>
		<Box />

		<Button
			variant="contained"
			size="large"
			fullWidth
			onClick={onCycleCrossHeadToolsClicked}
			data-pendoid="em-service__button__cycle-crosshead-tools"
		>
			<FormattedMessage id="Cycle CrossHead Tools" defaultMessage="Cycle CrossHead Tools" />
		</Button>
		<Button
			variant="contained"
			size="large"
			fullWidth
			onClick={onCycleLongHeadToolsClicked}
			data-pendoid="em-service__button__cycle-longhead-tools"
		>
			<FormattedMessage id="Cycle LongHead Tools" defaultMessage="Cycle LongHead Tools" />
		</Button>
		<Button
			variant="contained"
			size="large"
			fullWidth
			onClick={onCycleTrackToolsClicked}
			data-pendoid="em-service__button__cycle-tracks"
		>
			<FormattedMessage id="Cycle Tracks" defaultMessage="Cycle Track Tools" />
		</Button>
	</MachineServiceRow>
);

export interface EmServiceTemplateProps {
	readonly machine: EmServiceMachine;
	readonly onToggleLongHeadCreaseClicked: (longHeadNumber: number) => void;
	readonly onToggleLongHeadLatchClicked: (longHeadNumber: number) => void;
	readonly onToggleLongHeadCutClicked: (longHeadNumber: number) => void;
	readonly onToggleTrackBrakeClicked: (trackNumber: number) => void;
	readonly onToggleTrackRollerClicked: (trackNumber: number) => void;
	readonly onToggleCrossHeadCreaseClicked: () => void;
	readonly onToggleCrossHeadLatchClicked: () => void;
	readonly onToggleCrossHeadCutClicked: () => void;
	readonly onCycleCrossHeadToolsClicked: () => void;
	readonly onCycleLongHeadToolsClicked: () => void;
	readonly onCycleTrackToolsClicked: () => void;
}

export const EmServiceTemplate: React.FC<EmServiceTemplateProps> = ({
	machine,
	onToggleLongHeadCreaseClicked,
	onToggleLongHeadLatchClicked,
	onToggleLongHeadCutClicked,
	onToggleTrackBrakeClicked,
	onToggleTrackRollerClicked,
	onToggleCrossHeadCreaseClicked,
	onToggleCrossHeadLatchClicked,
	onToggleCrossHeadCutClicked,
	onCycleCrossHeadToolsClicked,
	onCycleLongHeadToolsClicked,
	onCycleTrackToolsClicked,
}) => (
	<Stack spacing={2}>
		<MachineServiceCard title={<FormattedMessage id="Cycle Tools" defaultMessage="Cycle Tools" />}>
			<CycleControl
				onCycleCrossHeadToolsClicked={onCycleCrossHeadToolsClicked}
				onCycleLongHeadToolsClicked={onCycleLongHeadToolsClicked}
				onCycleTrackToolsClicked={onCycleTrackToolsClicked}
			/>
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Cross Head" defaultMessage="Cross Head" />}>
			<CrossHeadControl
				machine={machine}
				onToggleCreaseClicked={onToggleCrossHeadCreaseClicked}
				onToggleLatchClicked={onToggleCrossHeadLatchClicked}
				onToggleCutClicked={onToggleCrossHeadCutClicked}
			/>
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Long Heads" defaultMessage="Long Heads" />}>
			<Stack spacing={2}>
				{(machine.physicalMachineSettings?.longHead?.longHeads ?? []).map((longhead) => (
					<LongHeadControl
						key={longhead.number}
						longhead={longhead}
						creaseTools={machine.creaseTools ?? []}
						cutTools={machine.cutTools ?? []}
						activeLatches={machine.activeLatches ?? []}
						onToggleCreaseClicked={onToggleLongHeadCreaseClicked}
						onToggleLatchClicked={onToggleLongHeadLatchClicked}
						onToggleCutClicked={onToggleLongHeadCutClicked}
					/>
				))}
			</Stack>
		</MachineServiceCard>

		<MachineServiceCard title={<FormattedMessage id="Tracks" defaultMessage="Tracks" />}>
			<Stack spacing={2}>
				{(machine.tracks ?? []).map((track) => (
					<TrackControl
						key={track.trackNumber}
						track={track}
						brakeTools={machine.brakeTools ?? []}
						rollerTools={machine.rollerTools ?? []}
						onToggleBrakeClicked={onToggleTrackBrakeClicked}
						onToggleRollerClicked={onToggleTrackRollerClicked}
					/>
				))}
			</Stack>
		</MachineServiceCard>
	</Stack>
);
